// third party imports
import {
  useState,
  useEffect,
  useContext,
  useRef,
  useMemo,
  type ChangeEvent,
  type MouseEvent,
  type ReactElement,
} from "react";
import { useNavigate } from "react-router-dom";
import { Avatar, message, Tooltip } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { Button, Form, Input, Select, DataTable } from "../../../shared";
import { InfoCircleOutlined, SearchOutlined } from "@ant-design/icons";
import {
  Eye,
  CreditCard,
  Trash2,
  Filter,
  ChevronDown,
  ChevronUp,
} from "react-feather";

// internal rebet imports
import styles from "./UserTable.module.css";
import createPrivateAdminPanelInstance from "../../../../api/PrivateAdminPanelInstance";
import { handleExpiredTokenError } from "../../../../utils/ErrorUtils";
import {
  NAVIGATION_ROUTES,
  USERS_FILTER_QUERY_PREFIX,
} from "../../../../utils/NavigationUtils";

// component imports
import CustomPagination from "../../../shared/CustomPagination/CustomPagination";
import DatePicker from "../../../shared/DatePicker/DatePicker";

// asset imports
import { API_ROUTES } from "../../../../api/ApiRoutes";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import AppContext from "../../../../context/AppContext";
import { convertToUserTimezone } from "../../../../utils/dateService";
import { AGENT_ACTIONS } from "../../../../utils/Constants";
import { objectToQueryParams } from "../../../../utils/QueryParamsUtils";
import useUsersFilterDropdowns from "../../../../queries/user-manager/useUsersFilterDropdowns";
import { useUsersExport } from "../../../../queries/user-manager/useUsersExport";
import { useUsersExportColumnCatalog } from "../../../../queries/user-manager/useUsersExportColumnCatalog";
import {
  buildUsersExportPayload,
  hasUnexportableFilters,
} from "../../../../utils/usersExportPayload";
import { getKycStatusPresentation } from "../../../../utils/kycStatus";
import {
  AsyncExportButton,
  type ExportColumnOption,
} from "../../../shared/AsyncExport";

import type {
  CachedPage,
  SorterState,
  UserRecord,
  UserRecordMatch,
  UsersFilterFormValues,
  UsersListParams,
  UsersListResponse,
} from "../../../../types/users";

type SelectOption = { value: string; label: string };

const SEARCH_BY_OPTIONS: SelectOption[] = [
  { value: "username", label: AppStrings.SEARCH_BY_USERNAME },
  { value: "email", label: AppStrings.SEARCH_BY_EMAIL },
  { value: "name", label: AppStrings.SEARCH_BY_NAME },
  { value: "user_id", label: AppStrings.SEARCH_BY_USER_ID },
  { value: "phone_number", label: AppStrings.SEARCH_BY_PHONE },
  {
    value: "referral_code_own",
    label: AppStrings.SEARCH_BY_REFERRAL_CODE_OWN,
  },
  { value: "referral_code", label: AppStrings.SEARCH_BY_REFERRAL_CODE_USED },
  { value: "utm_source", label: AppStrings.SEARCH_BY_UTM_SOURCE },
  { value: "utm_medium", label: AppStrings.SEARCH_BY_UTM_MEDIUM },
  { value: "utm_campaign", label: AppStrings.SEARCH_BY_UTM_CAMPAIGN },
];

// UTM filters use dedicated query params (`utm_source=x`), not `search=field:value`.
const UTM_SEARCH_FIELDS = new Set([
  "utm_source",
  "utm_medium",
  "utm_campaign",
]);
const USERS_LIST_FILTER_KEYS = [
  "status",
  "kyc_status",
  "ftd",
  "registration_state",
  "sports_bet_id",
  "casino_bet_id",
  "transaction_id",
  "registration_date_start",
  "registration_date_end",
  "last_evaluated_key",
  "sort_by",
  "sort_order",
] as const;
const USERS_FORM_FILTER_KEYS = [
  "status",
  "kyc_status",
  "ftd",
  "registration_state",
  "sports_bet_id",
  "casino_bet_id",
  "transaction_id",
] as const;
const SORTABLE_COLUMNS = new Set(["date_joined"]);
const USERS_FILTER_PARAM_PATTERN = new RegExp(
  `^${USERS_FILTER_QUERY_PREFIX}\\[([^\\]]+)\\]$`,
);

type InitialUsersFilters = {
  searchBy: string;
  searchValue: string;
  listParams: UsersListParams;
  formValues: UsersFilterFormValues;
};

const getInitialUsersFilters = (): InitialUsersFilters => {
  if (typeof window === "undefined") {
    return {
      searchBy: "username",
      searchValue: "",
      listParams: {},
      formValues: {},
    };
  }

  const params = new URLSearchParams(window.location.search);
  const filters: Record<string, string> = {};
  params.forEach((rawValue, paramName) => {
    const field = USERS_FILTER_PARAM_PATTERN.exec(paramName)?.[1];
    const value = rawValue.trim();
    if (field && value) filters[field] = value;
  });
  const searchOption = SEARCH_BY_OPTIONS.find(
    (option) => filters[option.value],
  );
  const listParams = Object.fromEntries(
    USERS_LIST_FILTER_KEYS.flatMap((key) =>
      filters[key] ? [[key, filters[key]]] : [],
    ),
  ) as UsersListParams;
  if (listParams.sort_order && !["asc", "desc"].includes(listParams.sort_order)) {
    delete listParams.sort_order;
  }
  if (listParams.sort_by && !SORTABLE_COLUMNS.has(listParams.sort_by)) {
    delete listParams.sort_by;
    delete listParams.sort_order;
  }
  const formValues = Object.fromEntries(
    USERS_FORM_FILTER_KEYS.flatMap((key) =>
      filters[key] ? [[key, filters[key]]] : [],
    ),
  ) as UsersFilterFormValues;

  return {
    searchBy: searchOption?.value ?? "username",
    searchValue: searchOption ? filters[searchOption.value] : "",
    listParams,
    formValues,
  };
};

// DataTable is JS-only; cast to a typed FC for the props this component uses.
// Mirrors the pattern in BetTable.tsx.
type UsersDataTableProps = {
  columns: Array<Record<string, unknown>>;
  dataSource: UserRecord[];
  pagination: false;
  rowKey: string;
  scrollPreset?: string;
  loading?: boolean;
  className?: string;
  onChange?: (
    pagination: unknown,
    filters: unknown,
    sorter: { field?: string | null; order?: "ascend" | "descend" | null },
  ) => void;
};
const TypedDataTable = DataTable as unknown as React.FC<UsersDataTableProps>;

type StoredToken = { AccessToken?: string };

type GetPageOptions = { isNextPage?: boolean; resetCache?: boolean };

type AxiosLikeError = {
  response?: { data?: { message?: string }; status?: number };
};

// Maps the BE `_match.matched_on` to the UserManager tab + the query-param key
// the destination sub-page reads (BetTable/CasinoBetsTable/TransactionTable
// each pick up their own param name from the URL).
const TAB_BY_MATCHED_ON: Record<string, string | undefined> = {
  sports_bet: "bets",
  casino_bet: "casino-bets",
  transaction: "transactions",
};
const ID_PARAM_BY_MATCHED_ON: Record<string, string | undefined> = {
  sports_bet: "betId",
  casino_bet: "betId",
  transaction: "txnId",
};

const ID_FILTER_KEYS = [
  "sports_bet_id",
  "casino_bet_id",
  "transaction_id",
] as const;

const UserTable = (): ReactElement => {
  // non-state variables
  const navigate = useNavigate();
  const tokenString = localStorage.getItem("token");
  const token: StoredToken | null = tokenString
    ? JSON.parse(tokenString)
    : null;
  const PrivateAdminPanelInstance = createPrivateAdminPanelInstance(
    token?.AccessToken,
  );
  const { adminActions } = useContext(AppContext) as {
    adminActions?: string[];
  };
  const canEditWallet = adminActions?.includes(AGENT_ACTIONS.EDIT_USER_WALLET);
  const canDeleteUsers = adminActions?.includes(
    AGENT_ACTIONS.SUSPEND_DISABLE_PAM_USER,
  );
  const canExportUsers = adminActions?.includes(AGENT_ACTIONS.USERS_EXPORT);
  const initialFilters = useMemo(getInitialUsersFilters, []);

  // state variables
  const [searchValue, setSearchValue] = useState<string>(
    initialFilters.searchValue,
  );
  const [searchBy, setSearchBy] = useState<string>(initialFilters.searchBy);
  const [usersToShowInTable, setUsersToShowInTable] = useState<UserRecord[]>(
    [],
  );
  const [cachedPages, setCachedPages] = useState<CachedPage[]>([]);
  const [currentPageIndex, setCurrentPageIndex] = useState<number>(0);
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState<string | null>(null);
  const [isLoadingUsers, setIsLoadingUsers] = useState<boolean>(false);
  const [sorterState, setSorterState] = useState<SorterState>(() => ({
    field: initialFilters.listParams.sort_by ?? null,
    order:
      initialFilters.listParams.sort_order === "asc"
        ? "ascend"
        : initialFilters.listParams.sort_order === "desc"
          ? "descend"
          : null,
  }));

  // Filter state variables
  const [showExtraFilters, setShowExtraFilters] = useState<boolean>(
    () =>
      Object.keys(initialFilters.formValues).length > 0 ||
      Boolean(
        initialFilters.listParams.registration_date_start ||
          initialFilters.listParams.registration_date_end,
      ),
  );
  const [filtersForm] = Form.useForm<UsersFilterFormValues>();
  const watchedFilterValues = Form.useWatch<UsersFilterFormValues | undefined>(
    [],
    filtersForm,
  );

  // Registration-date range lives outside the Form because its picker owns
  // its own state contract (dayjs tuple / null). Defaults to null — spec
  // requires the picker to start with no filtering applied.
  const [regDates, setRegDates] = useState<[Dayjs, Dayjs] | null>(() => {
    const start = initialFilters.listParams.registration_date_start;
    const end = initialFilters.listParams.registration_date_end;
    if (!start || !end) return null;

    const startDate = dayjs(start);
    const endDate = dayjs(end);
    return startDate.isValid() && endDate.isValid()
      ? [startDate, endDate]
      : null;
  });

  // Domain wrapper — pins the two Users export endpoints and error copy.
  // The AsyncExportButton drives it via `start()` at drawer submit time.
  const usersExportState = useUsersExport();

  // Pre-flight column catalog: GET the per-client vocabulary so the picker
  // reflects cash vs sweepstakes columns automatically and never shows a
  // key the BE will reject. `enabled` is gated on the permission so we
  // don't fire the request for admins who can't see the button.
  const {
    data: exportColumnCatalog,
    isLoading: isLoadingExportColumns,
    isError: isExportColumnsError,
  } = useUsersExportColumnCatalog({ enabled: Boolean(canExportUsers) });

  // Map the BE catalog into the shared `ExportColumnOption` shape.
  // `always_included` (e.g. `data_as_of`) becomes `locked: true` — the
  // picker renders those disabled + always-checked. Falls back to an empty
  // array while loading/erroring so the button disables gracefully via the
  // tooltip logic below.
  const exportColumns = useMemo<readonly ExportColumnOption[]>(() => {
    if (!exportColumnCatalog?.columns?.length) return [];
    return exportColumnCatalog.columns.map((col) => ({
      key: col.name,
      label: col.label,
      locked: col.always_included,
    }));
  }, [exportColumnCatalog]);

  const hasFilterValues = useMemo<boolean>(() => {
    if (!watchedFilterValues) return false;
    return Object.values(watchedFilterValues as Record<string, unknown>).some(
      (v) => {
        if (v == null) return false;
        if (typeof v === "string") return v.trim().length > 0;
        if (Array.isArray(v)) return v.length > 0;
        return Boolean(v);
      },
    );
  }, [watchedFilterValues]);

  // BE-driven filter options (5 min staleTime). Empty arrays while loading so
  // the Selects render without crashing.
  const { data: filterDropdowns, isLoading: isLoadingDropdowns } =
    useUsersFilterDropdowns();
  const statusOptions = filterDropdowns?.statuses ?? [];
  const kycOptions = filterDropdowns?.kyc_statuses ?? [];
  const ftdOptions = filterDropdowns?.ftd ?? [];
  const stateOptions = filterDropdowns?.states ?? [];

  // Ref for debounce timer
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isInitialMount = useRef<boolean>(true);
  const prevSearchValueRef = useRef<string>("");
  const [hasNextPage, setHasNextPage] = useState<boolean>(false);

  // use effects
  useEffect(() => {
    filtersForm.setFieldsValue(initialFilters.formValues);
    if (initialFilters.searchValue) {
      void searchUser();
    } else {
      void getPageOfUsers(initialFilters.listParams);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced search effect
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      prevSearchValueRef.current = searchValue;
      return;
    }

    const prev = prevSearchValueRef.current;
    prevSearchValueRef.current = searchValue;

    // dropdown changed but input is empty — don't refetch
    if (!searchValue?.trim() && !prev?.trim()) return;

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      if (searchValue?.trim()) {
        void searchUser();
      } else {
        void getPageOfUsers();
      }
    }, 800);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue, searchBy]);

  // api functions
  const getPageOfUsers = async (
    params: UsersListParams = {},
    options: GetPageOptions = {},
  ): Promise<UserRecord[]> => {
    const { isNextPage = false, resetCache = true } = options;
    setIsLoadingUsers(true);

    const queryStringParams = objectToQueryParams(params);

    try {
      const response = await PrivateAdminPanelInstance?.get(
        API_ROUTES?.GET_USERS + "?" + queryStringParams,
      );
      const body = response?.data as UsersListResponse | undefined;
      const data: UserRecord[] = body?.data ?? [];
      const newLastEvaluatedKey: string | null =
        body?.last_evaluated_key ?? null;
      const nextHasNextPage = Boolean(newLastEvaluatedKey);

      const pageData: CachedPage = {
        data,
        lastEvaluatedKey: newLastEvaluatedKey,
      };

      if (resetCache) {
        setCachedPages([pageData]);
        setCurrentPageIndex(0);
      } else if (isNextPage) {
        setCachedPages((prev) => [...prev, pageData]);
        setCurrentPageIndex((prev) => prev + 1);
      }

      setUsersToShowInTable(data);
      setLastEvaluatedKey(newLastEvaluatedKey);
      setHasNextPage(nextHasNextPage);
      return data;
    } catch (error) {
      const err = error as AxiosLikeError;
      message.error(
        err?.response?.data?.message || AppStrings.ERROR_FETCHING_USERS,
      );
      handleExpiredTokenError(navigate, err?.response);
      return [];
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const searchUser = async (
    user_id?: string | null,
    options: GetPageOptions = {},
  ): Promise<void> => {
    const { isNextPage = false, resetCache = true } = options;
    setIsLoadingUsers(true);
    const searchStr = user_id ? user_id : searchValue;

    try {
      // Build URL with optional last_evaluated_key for pagination.
      // Classic fields use `search=<field>:<value>`; UTM fields use `<field>=<value>`.
      const trimmedSearch = searchStr?.trim() ?? "";
      let url = API_ROUTES?.GET_USERS;
      if (trimmedSearch.length) {
        if (UTM_SEARCH_FIELDS.has(searchBy)) {
          const params = new URLSearchParams({
            [searchBy]: trimmedSearch,
          });
          url = `${API_ROUTES?.GET_USERS}?${params.toString()}`;
        } else {
          url = `${API_ROUTES?.SEARCH_USER}${searchBy}:${trimmedSearch}`;
        }
      }

      // Add last_evaluated_key if fetching next page
      if (isNextPage && lastEvaluatedKey) {
        url +=
          (url.includes("?") ? "&" : "?") +
          `last_evaluated_key=${encodeURIComponent(lastEvaluatedKey)}`;
      }

      const response = await PrivateAdminPanelInstance?.get(url);
      const body = response?.data as UsersListResponse | undefined;
      const data: UserRecord[] = body?.data ?? [];
      const newLastEvaluatedKey: string | null =
        body?.last_evaluated_key ?? null;
      const hasNext = Boolean(newLastEvaluatedKey);

      const pageData: CachedPage = {
        data,
        lastEvaluatedKey: newLastEvaluatedKey,
      };

      if (resetCache) {
        setCachedPages([pageData]);
        setCurrentPageIndex(0);
      } else if (isNextPage) {
        setCachedPages((prev) => [...prev, pageData]);
        setCurrentPageIndex((prev) => prev + 1);
      }

      setUsersToShowInTable(data);
      setLastEvaluatedKey(newLastEvaluatedKey);
      setHasNextPage(hasNext);
    } catch (error) {
      const err = error as AxiosLikeError;
      message.error(
        err?.response?.data?.message || AppStrings.ERROR_FETCHING_USERS,
      );
      handleExpiredTokenError(navigate, err?.response);
    } finally {
      setIsLoadingUsers(false);
    }
  };

  // Shared so handleApplyFilters, handleNextPressed, and the sort handler all
  // build the same filter shape — keeps list filter fields from drifting.
  // Registration-date bounds live outside the Form (picker state), so they're
  // merged in here rather than at each call site.
  const buildFilterParams = (
    values: UsersFilterFormValues = {},
  ): UsersListParams => {
    const trim = (v: unknown): string | undefined =>
      typeof v === "string" ? v.trim() : undefined;
    const params: UsersListParams = {};
    if (values?.status) params.status = values.status;
    if (values?.kyc_status) params.kyc_status = values.kyc_status;
    if (values?.ftd) params.ftd = values.ftd;
    const stateInput = trim(values?.registration_state);
    if (stateInput) params.registration_state = stateInput;
    const sportsBetId = trim(values?.sports_bet_id);
    if (sportsBetId) params.sports_bet_id = sportsBetId;
    const casinoBetId = trim(values?.casino_bet_id);
    if (casinoBetId) params.casino_bet_id = casinoBetId;
    const transactionId = trim(values?.transaction_id);
    if (transactionId) params.transaction_id = transactionId;
    if (regDates && regDates.length === 2) {
      params.registration_date_start = regDates[0].format("YYYY-MM-DD");
      params.registration_date_end = regDates[1].format("YYYY-MM-DD");
    }
    return params;
  };

  const handleClearFilters = (): void => {
    filtersForm.resetFields();
    setRegDates(null);
    setSorterState({ field: null, order: null });
    void getPageOfUsers();
  };

  // Build the `/users/manager?...` URL from a `_match` and navigate. No-op for
  // unknown `matched_on` values so a new BE enum bucket doesn't crash the FE.
  const redirectFromMatch = (record: UserRecord): boolean => {
    const match: UserRecordMatch | undefined = record._match?.[0];
    if (!match) return false;
    const tab = TAB_BY_MATCHED_ON[match.matched_on];
    const idParam = ID_PARAM_BY_MATCHED_ON[match.matched_on];
    if (!tab || !idParam) return false;
    const params = new URLSearchParams();
    params.set("userId", record.user_id);
    params.set("tab", tab);
    params.set(idParam, match.matched_id);
    if (match.bet_type) params.set("betType", match.bet_type);
    navigate(`${NAVIGATION_ROUTES?.USER_MANAGER}?${params.toString()}`);
    return true;
  };

  const hasIdFilter = (params: UsersListParams): boolean =>
    ID_FILTER_KEYS.some((k) => Boolean(params[k]));

  const handleApplyFilters = async (
    values: UsersFilterFormValues = {},
  ): Promise<void> => {
    setSorterState({ field: null, order: null });
    const params = buildFilterParams(values);
    const data = await getPageOfUsers(params);
    // Per ticket: only redirect when the user filtered by an ID and the BE
    // returned a single match. 0 → empty table; >1 → render as a list.
    if (hasIdFilter(params) && data.length === 1) {
      redirectFromMatch(data[0]);
    }
  };

  const submitClearedField = (field: keyof UsersFilterFormValues): void => {
    filtersForm.setFieldsValue({ [field]: null } as UsersFilterFormValues);
    filtersForm.submit();
  };

  // Fired by the shared DatePicker whenever its value changes (including a
  // clear). The picker's own `setDates` callback has already updated
  // `regDates`, but React batching means the closure below still reads the
  // stale reference — so we parse the emitted queryString directly. The
  // shared picker keys its output as `start_date/end_date`; the users v1
  // endpoint expects `registration_date_start/end`, so we re-key on the fly.
  const handleDateRangeChange = (queryString: string | null): void => {
    setSorterState({ field: null, order: null });
    const values = filtersForm.getFieldsValue() as UsersFilterFormValues;
    const baseParams = buildFilterParams(values);
    // Drop any dates already merged by buildFilterParams — the picker's
    // fresh values from the queryString are authoritative.
    delete baseParams.registration_date_start;
    delete baseParams.registration_date_end;
    if (queryString) {
      const parsed = new URLSearchParams(queryString);
      const s = parsed.get("start_date");
      const e = parsed.get("end_date");
      if (s) baseParams.registration_date_start = s;
      if (e) baseParams.registration_date_end = e;
    }
    void getPageOfUsers(baseParams);
  };

  // Build the shape the export endpoint accepts from the currently-active
  // list filters. Free-text search and ID filters are stripped upstream by
  // `buildUsersExportPayload`; the button is already disabled when they're
  // active, but the mapper is defence in depth for the network payload.
  const buildCurrentExportPayload = () => {
    const values = filtersForm.getFieldsValue() as UsersFilterFormValues;
    return buildUsersExportPayload({
      ...values,
      registration_date_start: regDates?.[0]?.format("YYYY-MM-DD"),
      registration_date_end: regDates?.[1]?.format("YYYY-MM-DD"),
    });
  };

  // Mirror of the BE 422 vocabulary — free-text search + ID filters can't be
  // exported. Feed both the form state and the free-text search value so the
  // button's disabled/tooltip UI matches what the BE would enforce.
  const currentUnexportableActive = useMemo<boolean>(() => {
    const values = filtersForm.getFieldsValue() as UsersFilterFormValues;
    return hasUnexportableFilters({ ...values, search: searchValue });
  }, [watchedFilterValues, searchValue]); // eslint-disable-line react-hooks/exhaustive-deps

  // helper functions
  const handleSearchValueChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setSearchValue(e.target.value);
  };

  const handleSearchByChange = (value: string): void => {
    setSearchBy(value);
  };

  const goToCachedPage = (pageIndex: number): void => {
    const cachedPage = cachedPages[pageIndex];
    if (!cachedPage) return;

    setUsersToShowInTable(cachedPage.data);
    setLastEvaluatedKey(cachedPage.lastEvaluatedKey);
    setHasNextPage(
      Boolean(cachedPage.lastEvaluatedKey) ||
        cachedPages.length > pageIndex + 1,
    );
    setCurrentPageIndex(pageIndex);
  };

  const handleNextPressed = (): void => {
    const nextPageIndex = currentPageIndex + 1;

    if (cachedPages[nextPageIndex]) {
      goToCachedPage(nextPageIndex);
    } else {
      // Use searchUser if in search mode, otherwise use getPageOfUsers
      if (searchValue?.trim()) {
        void searchUser(null, { isNextPage: true, resetCache: false });
      } else {
        const values = filtersForm.getFieldsValue() as UsersFilterFormValues;
        const params: UsersListParams = {
          ...buildFilterParams(values),
          last_evaluated_key: lastEvaluatedKey,
        };
        void getPageOfUsers(params, { isNextPage: true, resetCache: false });
      }
    }
  };

  const handlePageSelect = (pageNumber: number): void => {
    const targetPageIndex = pageNumber - 1;

    if (targetPageIndex === currentPageIndex) return;

    if (targetPageIndex < cachedPages.length) {
      goToCachedPage(targetPageIndex);
      return;
    }

    if (targetPageIndex === cachedPages.length && hasNextPage) {
      handleNextPressed();
    }
  };

  const handleViewUser = (
    user: UserRecord,
    e?: MouseEvent<HTMLElement>,
  ): void => {
    e?.stopPropagation();
    navigate(`${NAVIGATION_ROUTES?.USER_MANAGER}?userId=${user.user_id}`, {
      state: { user_data: user, wallet_active: false },
    });
  };

  const handleEditWallet = (
    user: UserRecord,
    e?: MouseEvent<HTMLElement>,
  ): void => {
    e?.stopPropagation();
    navigate(
      `${NAVIGATION_ROUTES?.USER_MANAGER}?userId=${user.user_id}&open=user_wallet`,
      { state: { user_data: user, wallet_active: false } },
    );
  };

  const handleDeleteUser = (
    user: UserRecord,
    e?: MouseEvent<HTMLElement>,
  ): void => {
    e?.stopPropagation();
    navigate(
      `${NAVIGATION_ROUTES?.USER_MANAGER}?userId=${user.user_id}&open=delete_user`,
      { state: { user_data: user, wallet_active: false } },
    );
  };

  const getUserStatus = (user: UserRecord): string => {
    if (
      user?.is_deleted === "1" ||
      user?.is_deleted === 1 ||
      user?.is_deleted === true
    ) {
      return AppStrings.DELETED;
    }
    if (
      user?.suspended === "1" ||
      user?.suspended === 1 ||
      user?.suspended === true
    ) {
      return AppStrings.SUSPENDED;
    }
    return AppStrings.ACTIVE_FILTER_OPTION;
  };

  const formatDate = (dateString?: string | null): string => {
    if (!dateString) return "-";
    return convertToUserTimezone(dateString, "MM/DD/YY hh:mm A");
  };

  // Table columns — kept loosely typed to match the antd/DataTable column
  // contract (BetTable.tsx uses the same `Array<Record<string, any>>` shape).
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns: Array<Record<string, any>> = [
    {
      title: AppStrings.REGISTRATION_DATE,
      dataIndex: "date_joined",
      key: "date_joined",
      width: 200,
      sorter: true,
      sortOrder: sorterState.field === "date_joined" ? sorterState.order : null,
      render: (text: string | undefined) => <span>{formatDate(text)}</span>,
    },
    {
      title: AppStrings.USER_ID,
      dataIndex: "user_id",
      key: "user_id",
      width: 150,
      render: (text: string | undefined) => (
        <span className={styles.userIdText}>{text}</span>
      ),
    },
    {
      title: AppStrings.USERNAME,
      dataIndex: "username",
      key: "username",
      width: 200,
      render: (text: string | undefined, record: UserRecord) => (
        <div
          className={styles.usernameCell}
          onClick={() => handleViewUser(record)}
          style={{ cursor: "pointer" }}
        >
          <Avatar
            size={28}
            src={record?.profile_photo}
            style={{ backgroundColor: "var(--COLOR-PRIMARY)", flexShrink: 0 }}
          >
            {text?.charAt(0)?.toUpperCase()}
          </Avatar>
          <span className={styles.hoverableText}>{text}</span>
        </div>
      ),
    },
    {
      title: AppStrings.EMAIL,
      dataIndex: "email",
      key: "email",
      width: 150,
      render: (text: string | undefined) => (
        <span className={styles.hoverableText}>{text}</span>
      ),
    },
    {
      title: AppStrings.FIRST_NAME,
      dataIndex: "first_name",
      key: "first_name",
      width: 150,
      render: (text: string | undefined) => <span>{text || "-"}</span>,
    },
    {
      title: AppStrings.LAST_NAME,
      dataIndex: "last_name",
      key: "last_name",
      width: 150,
      render: (text: string | undefined) => <span>{text || "-"}</span>,
    },
    {
      title: AppStrings.PHONE,
      dataIndex: "phone_number",
      key: "phone_number",
      width: 150,
      render: (text: string | undefined) => <span>{text || "-"}</span>,
    },
    {
      title: AppStrings.KYC,
      dataIndex: "kyc_status",
      key: "kyc_status",
      width: 100,
      align: "center",
      render: (text: string | undefined) => {
        const status = getKycStatusPresentation(text);
        return (
          <Tooltip title={status.label}>
            <span
              className={`${styles.kycBadge} ${styles[`kycBadge_${status.tone}`]}`}
            >
              {status.label}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: AppStrings.STATUS,
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (_: unknown, record: UserRecord) => {
        const status = getUserStatus(record);
        let statusClass = `${styles.statusBadge} ${styles.statusActive}`;
        if (status === AppStrings.SUSPENDED)
          statusClass = `${styles.statusBadge} ${styles.statusSuspended}`;
        if (status === AppStrings.DELETED)
          statusClass = `${styles.statusBadge} ${styles.statusDeleted}`;
        return <span className={statusClass}>{status}</span>;
      },
    },
    {
      title: AppStrings.FTD,
      dataIndex: "initial_deposit_ts",
      key: "ftd",
      width: 100,
      render: (text: string | null | undefined) => (
        <span className={text ? styles.ftdYes : styles.ftdNo}>
          {text ? AppStrings.YES : AppStrings.NO}
        </span>
      ),
    },
    {
      title: AppStrings.REFERRAL_CODE_OWN,
      dataIndex: "referral_code_own",
      key: "referral_code_own",
      width: 150,
      render: (text: string | null | undefined) => <span>{text || "-"}</span>,
    },
    {
      title: AppStrings.REFERRAL_CODE_USED,
      dataIndex: "referral_code",
      key: "referral_code_used",
      width: 150,
      render: (text: string | null | undefined) => <span>{text || "-"}</span>,
    },
    {
      title: AppStrings.UTM_SOURCE,
      dataIndex: "utm_source",
      key: "utm_source",
      width: 140,
      render: (text: string | null | undefined) => <span>{text || "-"}</span>,
    },
    {
      title: AppStrings.UTM_MEDIUM,
      dataIndex: "utm_medium",
      key: "utm_medium",
      width: 140,
      render: (text: string | null | undefined) => <span>{text || "-"}</span>,
    },
    {
      title: AppStrings.UTM_CAMPAIGN,
      dataIndex: "utm_campaign",
      key: "utm_campaign",
      width: 160,
      render: (text: string | null | undefined) => <span>{text || "-"}</span>,
    },
    {
      title: AppStrings.LOCATION,
      dataIndex: "new_terms_accepted",
      key: "location",
      width: 150,
      render: (
        newTerms: { location?: string } | null | undefined,
        record: UserRecord,
      ) => {
        const location =
          newTerms?.location || record?.registration_state || "-";
        return <span>{location || "-"}</span>;
      },
    },
    {
      title: AppStrings.ACTIONS,
      key: "actions",
      fixed: "right",
      width: 150,
      render: (_: unknown, record: UserRecord) => (
        <div className={styles.actionsCell}>
          <button
            type="button"
            className={`${styles.actionBtn} ${styles.viewBtn}`}
            onClick={(e) => handleViewUser(record, e)}
            title={AppStrings.VIEW_USER}
          >
            <Eye size={16} />
          </button>
          {canEditWallet && (
            <button
              type="button"
              className={`${styles.actionBtn} ${styles.walletBtn}`}
              onClick={(e) => handleEditWallet(record, e)}
              title={AppStrings.EDIT_WALLET}
            >
              <CreditCard size={16} />
            </button>
          )}
          {canDeleteUsers && (
            <button
              type="button"
              className={`${styles.actionBtn} ${styles.deleteBtn}`}
              onClick={(e) => handleDeleteUser(record, e)}
              title={AppStrings.DELETE_USER}
            >
              <Trash2 size={16} />
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className={styles.usersTableContainer}>
      <div className={styles.usersHeader}>
        <div className={styles.usersHeaderActions}>
          <div
            className={styles.usersDateRange}
            data-testid="users-date-range-picker"
          >
            <DatePicker
              dates={regDates}
              setDates={setRegDates}
              getData={handleDateRangeChange}
              allowClearToNull
            />
            <Tooltip
              title={AppStrings.USERS_REGISTRATION_DATE_FILTER_INFO}
              placement="top"
              color="var(--COLOR-PRIMARY_BACKGROUND_COLOR)"
            >
              <InfoCircleOutlined
                className={styles.dateRangeInfoIcon}
                data-testid="users-date-range-info-icon"
                aria-label={AppStrings.USERS_REGISTRATION_DATE_FILTER_INFO}
              />
            </Tooltip>
          </div>

          {canExportUsers && (
            <AsyncExportButton
              columns={exportColumns}
              buildPayload={buildCurrentExportPayload}
              exportState={usersExportState}
              // Precedence: unexportable-filters guard (existing product
              // rule) → catalog error (BE unreachable) → catalog loading
              // (empty picker would confuse the admin). The button also
              // auto-disables on `isBusy`, so no explicit check here.
              disabled={
                currentUnexportableActive ||
                isExportColumnsError ||
                isLoadingExportColumns ||
                exportColumns.length === 0
              }
              disabledTooltip={
                currentUnexportableActive
                  ? AppStrings.USERS_EXPORT_TOOLTIP_UNSUPPORTED_FILTERS
                  : isExportColumnsError
                    ? AppStrings.USERS_EXPORT_COLUMNS_ERROR
                    : isLoadingExportColumns
                      ? AppStrings.USERS_EXPORT_COLUMNS_LOADING
                      : undefined
              }
              labels={{
                buttonAriaLabel: AppStrings.USERS_EXPORT_BUTTON_ARIA,
              }}
            />
          )}
        </div>
      </div>

      <div className={styles.usersSearchBar}>
        <Select
          value={searchBy}
          onChange={handleSearchByChange}
          options={SEARCH_BY_OPTIONS}
          className={styles.usersSearchDropdown}
          classNames={{ popup: { root: "users-dropdown-popup" } }}
          suffixIcon={<ChevronDown size={14} color="var(--grey-400)" />}
          virtual={false}
        />

        <div className={styles.usersSearchWrapper}>
          <Input
            placeholder={AppStrings.SEARCH}
            prefix={
              <SearchOutlined
                style={{ fontSize: "16px", color: "var(--grey-400)" }}
              />
            }
            value={searchValue}
            onChange={handleSearchValueChange}
            className={styles.usersSearchInput}
            allowClear
          />
        </div>

        <div
          className={styles.filterButtons}
          data-testid="filters-toggle"
          onClick={() => setShowExtraFilters(!showExtraFilters)}
        >
          <Filter className={styles.filterIcon} size={18} />
          {showExtraFilters ? (
            <ChevronUp className={styles.filterIcon} size={18} />
          ) : (
            <ChevronDown className={styles.filterIcon} size={18} />
          )}
        </div>
      </div>

      <Form form={filtersForm} onFinish={handleApplyFilters} component={false}>
        <div
          className={styles.usersFiltersContainer}
          data-testid="filters-panel"
          style={{ display: showExtraFilters ? "flex" : "none" }}
        >
          {/* Row 1: 4 Selects evenly distributed */}
          <div className={styles.usersSelectors}>
            <div className={styles.usersSelector}>
              <span>{AppStrings.STATUS}</span>
              <Form.Item name="status" noStyle>
                <Select
                  placeholder={AppStrings.NOT_SELECTED}
                  allowClear
                  loading={isLoadingDropdowns}
                  onClear={() => submitClearedField("status")}
                  options={statusOptions}
                  className={styles.usersFilterSelect}
                />
              </Form.Item>
            </div>
            <div className={styles.usersSelector}>
              <span>{AppStrings.KYC_STATUS}</span>
              <Form.Item name="kyc_status" noStyle>
                <Select
                  placeholder={AppStrings.NOT_SELECTED}
                  allowClear
                  loading={isLoadingDropdowns}
                  onClear={() => submitClearedField("kyc_status")}
                  options={kycOptions}
                  className={styles.usersFilterSelect}
                />
              </Form.Item>
            </div>
            <div className={styles.usersSelector}>
              <span>{AppStrings.FTD}</span>
              <Form.Item name="ftd" noStyle>
                <Select
                  placeholder={AppStrings.NOT_SELECTED}
                  allowClear
                  loading={isLoadingDropdowns}
                  onClear={() => submitClearedField("ftd")}
                  options={ftdOptions}
                  className={styles.usersFilterSelect}
                />
              </Form.Item>
            </div>
            <div className={styles.usersSelector}>
              <span>{AppStrings.REGISTRATION_STATE}</span>
              <Form.Item name="registration_state" noStyle>
                <Select
                  placeholder={AppStrings.NOT_SELECTED}
                  allowClear
                  showSearch
                  loading={isLoadingDropdowns}
                  onClear={() => submitClearedField("registration_state")}
                  options={stateOptions}
                  optionFilterProp="label"
                  className={styles.usersFilterSelect}
                />
              </Form.Item>
            </div>
          </div>

          {/* Row 2: 3 ID Inputs */}
          <div className={styles.usersSelectors}>
            <div className={styles.usersSelector}>
              <span>{AppStrings.SPORTS_BET_ID}</span>
              <Form.Item name="sports_bet_id" noStyle>
                <Input placeholder={AppStrings.SPORTS_BET_ID} allowClear />
              </Form.Item>
            </div>
            <div className={styles.usersSelector}>
              <span>{AppStrings.CASINO_BET_ID}</span>
              <Form.Item name="casino_bet_id" noStyle>
                <Input placeholder={AppStrings.CASINO_BET_ID} allowClear />
              </Form.Item>
            </div>
            <div className={styles.usersSelector}>
              <span>{AppStrings.TRANSACTION_ID}</span>
              <Form.Item name="transaction_id" noStyle>
                <Input placeholder={AppStrings.TRANSACTION_ID} allowClear />
              </Form.Item>
            </div>
          </div>
        </div>

        <div
          className={styles.usersSelectorActionButtons}
          style={{ display: showExtraFilters ? "flex" : "none" }}
        >
          <Button
            intent="neutral"
            variant="outline"
            size="large"
            onClick={handleClearFilters}
          >
            {AppStrings.CLEAR_FILTERS}
          </Button>
          <Button
            intent="primary"
            size="large"
            onClick={() => filtersForm.submit()}
            disabled={!hasFilterValues}
          >
            {AppStrings.APPLY}
          </Button>
        </div>
      </Form>

      <div className={styles.usersTableWrapper}>
        <TypedDataTable
          columns={columns}
          dataSource={usersToShowInTable}
          pagination={false}
          rowKey="user_id"
          scrollPreset="wide"
          loading={isLoadingUsers}
          className={styles.usersAntTable}
          onChange={(_p, _f, sorter) => {
            const field = sorter?.field ?? null;
            const order = sorter?.order ?? null;

            setSorterState({ field, order });

            // Preserve filter values on sort; only include sort_by/sort_order
            // when there's an active sort (antd's third click clears it).
            const values =
              filtersForm.getFieldsValue() as UsersFilterFormValues;
            const params: UsersListParams = buildFilterParams(values);
            if (order && field) {
              params.sort_by = field;
              params.sort_order = order === "ascend" ? "asc" : "desc";
            }

            void getPageOfUsers(params);
          }}
        />
      </div>

      <div className={styles.usersPaginationContainer}>
        <CustomPagination
          total={cachedPages.length + (hasNextPage ? 1 : 0)}
          pageSize={1}
          currentPage={currentPageIndex + 1}
          onPageChange={handlePageSelect}
          onPageSizeChange={() => {}}
          showPageSizeSelector={false}
          itemLabel="pages"
          restrictToAdjacentPages
        />
      </div>
    </div>
  );
};

export default UserTable;
